import { Toaster, toast } from 'sonner';
import React from 'react';

/**
 * Toast Provider Component
 * Add this to App.tsx root to enable toast notifications
 */
export const ToastProvider: React.FC = () => (
  <Toaster
    position="bottom-right"
    expand={true}
    richColors
    closeButton
    duration={4000}
    toastOptions={{
      className: 'font-sans',
      style: {
        background: 'var(--toast-bg, rgba(255, 255, 255, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--toast-border, rgba(0, 0, 0, 0.1))',
        color: 'var(--toast-text, #000)',
      },
      classNames: {
        title: 'font-semibold',
        description: 'text-sm opacity-80',
        actionButton: 'bg-william-600 text-white hover:bg-william-700',
        cancelButton: 'bg-gray-200 text-gray-900 hover:bg-gray-300',
      },
    }}
  />
);

/** A button on a toast, e.g. "Get more" on a not-enough styles and codes error. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Toast Utility Functions
 * Use these throughout the app for consistent notifications
 */
export const showToast = {
  /**
   * Success toast - green checkmark
   */
  success: (message: string, description?: string) =>
    toast.success(message, { description }),

  /**
   * Error toast - red X, with an optional action button
   */
  error: (message: string, description?: string, action?: ToastAction) =>
    toast.error(message, { description, ...(action ? { action } : {}) }),

  /**
   * Info toast - blue info icon
   */
  info: (message: string, description?: string) =>
    toast.info(message, { description }),

  /**
   * Warning toast - yellow warning icon
   */
  warning: (message: string, description?: string) =>
    toast.warning(message, { description }),

  /**
   * Loading toast - spinner
   * Returns toast ID for dismissal
   */
  loading: (message: string) =>
    toast.loading(message),

  /**
   * Promise toast - automatically handles loading/success/error states
   */
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) => toast.promise(promise, messages),

  /**
   * Custom toast with action button
   */
  custom: (message: string, options?: {
    description?: string;
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }) => {
    const { description, action, duration } = options || {};
    return toast(message, {
      description,
      duration,
      action: action ? {
        label: action.label,
        onClick: action.onClick,
      } : undefined,
    });
  },
};

/**
 * Dismiss a specific toast by ID
 */
export const dismissToast = (toastId: string | number) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};
